import { Injectable } from '@angular/core';

export interface OrderItem<T> {
  value: T;
  order: string;
}

const LEFT_MOST = 96;
const RIGHT_MOST = 123;

@Injectable({ providedIn: 'root' })
export class LexicalOrdering {
  initialiseRecursively<T>(
    list: readonly T[],
    start: string,
    end: string,
  ): readonly OrderItem<T>[] {
    if (list.length === 0) {
      return [];
    }
    if (list.length === 1) {
      return [{ value: list[0], order: this.getMiddle(start, end) }];
    }

    const midIdx = Math.floor(list.length / 2);
    const midOrder = this.getMiddle(start, end);
    const left = this.initialiseRecursively(
      list.slice(0, midIdx),
      start,
      midOrder,
    );
    const right = this.initialiseRecursively(
      list.slice(midIdx + 1),
      midOrder,
      end,
    );
    return [...left, { value: list[midIdx], order: midOrder }, ...right];
  }

  /**
   * 1. basic case
   * abcde ~ abchi -> abc + d ~ h -> abcf (d ~ h = f as f is between d and h)
   * abc ~ abchi -> abc + _ ~ h -> abcd
   *
   * 2. consecutive characters
   * abhs ~ abit -> ab + h ~ i -> abh + s ~ _ -> abhw
   * abh ~ abit -> ab + h ~ i -> abh + _ ~ _ -> abhm
   * abhzz ~ abit -> ab + h ~ i -> abh + z ~ _ -> abhz + z ~ _ -> abhzz + _ ~ _ -> abhzzm
   *
   * 3. right character is a or b
   * abc ~ abcah -> abc + _ ~ a -> abca + _ ~ h -> abcad
   * abc ~ abcab -> abc + _ ~ a -> abca + _ ~ b -> abcaa + _ ~ _ -> abcaam
   * abc ~ abcb -> abc + _ ~ b -> abca + _ ~ _  -> abcam
   *
   * @param start
   * @param end
   */
  getMiddle(start: string, end: string): string {
    if (start >= end && start.length * end.length !== 0) {
      throw new Error(`Cannot insert into ${start} and ${end}`);
    }

    // find the first character that mismatches
    let idx = 0;
    while (
      idx < Math.min(start.length, end.length) &&
      start.charCodeAt(idx) === end.charCodeAt(idx)
    ) {
      idx += 1;
    }

    let result = start.slice(0, idx);
    let left = idx < start.length ? start.charCodeAt(idx) : LEFT_MOST;
    let right = idx < end.length ? end.charCodeAt(idx) : RIGHT_MOST;

    if (right - left === 1 && left !== LEFT_MOST) {
      // if the character from start string is legit and
      // the characters from two strings are consecutive,
      // just append the character from start string to
      // the result, and append all the 'z' to the result
      // as well if there is any
      do {
        result += String.fromCharCode(left);
        idx += 1;
        left = idx < start.length ? start.charCodeAt(idx) : LEFT_MOST;
      } while (left === RIGHT_MOST - 1);
      // the upper bound for the last character can be
      // safely set to RIGHT_MOST
      right = RIGHT_MOST;
    } else {
      // if the character from the end string is 'a',
      // append all the 'a's to the reuslt
      while (right - LEFT_MOST === 1) {
        result += String.fromCharCode(right);

        idx += 1;
        left = LEFT_MOST;
        right = idx < end.length ? end.charCodeAt(idx) : RIGHT_MOST;
      }
    }

    // get the middle of lower and upper bounds as the last character
    const lastChar = String.fromCharCode(Math.floor((left + right) / 2));
    result += lastChar;

    // if last character is 'a', that would create two
    // consecutive strings, eg. no strings can be inserted
    // into 'a' and 'aa'. So we need to insert an
    // additional character here.
    if (lastChar === 'a') {
      result += String.fromCharCode(Math.floor((LEFT_MOST + RIGHT_MOST) / 2));
    }
    return result;
  }
}
